import { getBasePath } from "../hyperbase/utils/base-url";

export function injectLogo({
  author,
}: {
  author?: {
    id: string;
    username: string;
    avatar_url: string | null;
  };
}) {
  const hideMenu =
    new URL(window.location.href).searchParams.get("hide_menu") === "true";

  const top = new URL(window.location.href).searchParams.get("top") === "true";
  const left =
    new URL(window.location.href).searchParams.get("left") === "true";

  const avatarUrl = author?.avatar_url;

  const bottom = !top;
  const right = !left;

  const showMenu = !hideMenu;
  // if query param __websim_screenshot_mode is true, don't inject the logo
  if (
    new URL(window.location.href).searchParams.get(
      "__websim_screenshot_mode"
    ) === "true"
  ) {
    return;
  }

  // Only inject logo once when page loads
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () =>
      injectLogoToPage({
        showMenu,
        bottom,
        right,
        avatarUrl: avatarUrl ?? null,
      })
    );
  } else {
    injectLogoToPage({ showMenu, bottom, right, avatarUrl: avatarUrl ?? null });
  }
}

function injectLogoToPage({
  showMenu,
  bottom,
  right,
  avatarUrl,
}: {
  showMenu: boolean;
  bottom: boolean;
  right: boolean;
  avatarUrl: string | null;
}) {
  const logoContainer = document.createElement("div");
  document.body.appendChild(logoContainer);
  // Create a shadow root if useShadowDOM is true, otherwise use the container directly
  const useShadowDOM = true;
  const target = useShadowDOM
    ? logoContainer.attachShadow({ mode: "open" })
    : logoContainer;

  const logoHTML = `
<a id="websim-logo-container">
    <div id="websim-close-button">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
    </div>
    <div id="websim-logo">
        <span>w</span><span>e</span><span>b</span><span>s</span><span>i</span><span>m</span>
    </div>
    ${
      showMenu
        ? avatarUrl
          ? `<img src="${avatarUrl}" class="size-5" style="border-radius: 100%; border: 1px solid rgb(64, 64, 64);"/>`
          : `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5">
        <path fill-rule="evenodd" d="M9.47 6.47a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 1 1-1.06 1.06L10 8.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06l4.25-4.25Z" clip-rule="evenodd" />
    </svg>
    `
        : ``
    }
</a>
  `;

  // Create styles element
  const styles = document.createElement("style");
  styles.textContent = `
  #websim-logo-container {
    position: fixed;
    z-index: 2147483647;
    ${bottom ? "bottom: 0;" : "top: 0;"}
    ${right ? "right: 0;" : "left: 0;"}
    margin: 0.25rem;
    color: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    transform: scale(0) rotate(0deg);
    transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    backdrop-filter: blur(10px);
    background-color: rgba(20, 20, 20, 0.9);
    border-radius: 2rem;
    backdrop-filter: blur(10px);
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
    font-family: Helvetica, sans-serif;
    letter-spacing: 0.0em;
    font-size: 0.8rem;
    text-decoration: none;
    padding: 0.4rem 0.8rem;
    gap: 0.8rem;
  }

  .size-5 {
    width: 1.375rem; /* 22px */
    height: 1.375rem; /* 22px */
  }

  #websim-logo-container svg {
    border-radius: 100%;
    padding: 0.2rem;
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  }

  #websim-logo-container.show {
    transform: scale(1) rotate(0deg);
  }

  #websim-logo {
    padding: 0rem 0rem 0.2rem 0.2rem;
    font-family: "Comic Sans MS", cursive;
    font-size: 1rem;
    font-weight: bold;
    text-shadow: 0.04em 0.04em 0.08em rgba(0, 0, 0, 0.25);
    letter-spacing: 0.2em;
    text-decoration: none;
  }

  #websim-logo span {
    display: inline-block;
  }

  #websim-logo span:nth-child(2) {
    transform: rotate(5deg) translateY(-0.2rem);
  }

  #websim-logo span:nth-child(3) {
    transform: rotate(-5deg) translateY(0.1rem);
  }

  #websim-logo span:nth-child(4) {
    transform: rotate(10deg) translateY(-0.1rem);
  }

  #websim-logo span:nth-child(5) {
    transform: rotate(-10deg) translateY(0.2rem);
  }

  #websim-logo span:nth-child(6) {
    transform: rotate(5deg) translateY(-0.1rem);
  }

  #websim-logo span:nth-child(1) { color: #4285f4; }
  #websim-logo span:nth-child(2) { color: #ea4335; }
  #websim-logo span:nth-child(3) { color: #fbbc05; }
  #websim-logo span:nth-child(4) { color: #4285f4; }
  #websim-logo span:nth-child(5) { color: #34a853; }
  #websim-logo span:nth-child(6) { color: #ea4335; }

  @media (prefers-color-scheme: dark) {
    #websim-logo span:nth-child(1) { color: #FEF08A; }
    #websim-logo span:nth-child(2) { color: #99F6E4; }
    #websim-logo span:nth-child(3) { color: #38BDF8; }
    #websim-logo span:nth-child(4) { color: #FEF08A; }
    #websim-logo span:nth-child(5) { color: #F9A8D4; }
    #websim-logo span:nth-child(6) { color: #99F6E4; }
  }

  .size-4 {
    width: 0.75rem;
    height: 0.75rem;
  }

  #websim-close-button {
    position: absolute;
    top: -0.25rem;
    right: -0.25rem;
    cursor: pointer;
    background-color: rgba(20, 20, 20, 0.9);
    border-radius: 50%;
    padding: 0.15rem;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  /* Always show close button on mobile/touch devices */
  @media (hover: none) {
    #websim-close-button {
      opacity: 1;
    }
  }

  #websim-logo-container:hover #websim-close-button {
    opacity: 1;
  }

  `;

  // Append style and logo HTML to target (shadow DOM or container)
  target.appendChild(styles);
  const logo = document.createElement("div");
  logo.innerHTML = logoHTML;
  target.appendChild(logo);

  const showAnimation = () => {
    const logoElement = target.querySelector("#websim-logo-container");
    logoElement?.classList.add("show");
  };

  // If there's an avatar image, wait for it to load before showing animation
  const avatarImg = target.querySelector("img");
  if (avatarImg) {
    avatarImg.onload = () =>
      requestAnimationFrame(() => setTimeout(showAnimation, 1000));
  } else {
    requestAnimationFrame(showAnimation);
  }

  const link = logo.querySelector("#websim-logo-container");
  if (link) {
    link.setAttribute("href", makeLink());
  }

  const closeButton = target.querySelector("#websim-close-button");
  if (closeButton) {
    closeButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      logoContainer.remove();
    });
  }
}

function makeLink(): string {
  const route = __WEBSIM_DATA__.route;
  const project_id = __WEBSIM_DATA__.project_id;

  const url = new URL(__WEBSIM_DATA__.hostOrigin);
  url.search = window.location.search;
  url.searchParams.set("__websim_pin_sidebar", "true");

  if (route) {
    url.pathname = getBasePath(route);
  } else if (project_id) {
    url.pathname = getBasePath({ type: "project", id: project_id });
  } else {
    url.pathname = "/";
  }

  // Copy over hash from current URL
  url.hash = window.location.hash;

  return url.toString();
}
